import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, Trash2, User } from 'lucide-react';
import apiClient from '../../api/client';

const OpportunityComments = ({ opportunityId }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [users, setUsers] = useState([]);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (opportunityId) {
      loadComments();
      loadUsers();
    }
  }, [opportunityId]);

  const loadComments = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/v1/pipeline/opportunities/${opportunityId}/comments`);
      if (response.data.success) {
        setComments(response.data.data);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      // Load users for @mentions from the pipeline users search endpoint
      const response = await apiClient.get(`/api/v1/pipeline/opportunities/${opportunityId}/users/search`, {
        params: { q: '', limit: 50 }
      });
      if (response.data.success) {
        // Map the response to include first_name and last_name for display
        const mappedUsers = (response.data.data || []).map(user => ({
          ...user,
          first_name: user.name?.split(' ')[0] || '',
          last_name: user.name?.split(' ').slice(1).join(' ') || ''
        }));
        setUsers(mappedUsers);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    try {
      setSubmitting(true);
      
      const response = await apiClient.post(
        `/api/v1/pipeline/opportunities/${opportunityId}/comments`,
        null,
        {
          params: {
            comment_text: commentText.trim()
          }
        }
      );

      if (response.data.success) {
        setCommentText('');
        await loadComments();
      } else {
        alert('Failed to post comment');
      }
    } catch (error) {
      console.error('Error posting comment:', error);
      alert('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      const response = await apiClient.delete(
        `/api/v1/pipeline/opportunities/${opportunityId}/comments/${commentId}`
      );

      if (response.data.success) {
        await loadComments();
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment');
    }
  };

  const handleTextChange = (e) => {
    const text = e.target.value;
    setCommentText(text);

    // Check for @ mention trigger
    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = text.slice(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
      
      // Check if we're in a mention (no space after @)
      if (!textAfterAt.includes(' ') && textAfterAt.length < 50) {
        setMentionSearch(textAfterAt);
        setShowMentions(true);
        
        // Calculate position for dropdown
        if (textareaRef.current) {
          const rect = textareaRef.current.getBoundingClientRect();
          setMentionPosition({
            top: rect.top - 200,
            left: rect.left
          });
        }
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (user) => {
    const cursorPosition = textareaRef.current.selectionStart;
    const textBeforeCursor = commentText.slice(0, cursorPosition);
    const textAfterCursor = commentText.slice(cursorPosition);
    
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    const newText = 
      textBeforeCursor.slice(0, lastAtIndex) + 
      `@${user.email.split('@')[0]} ` + 
      textAfterCursor;
    
    setCommentText(newText);
    setShowMentions(false);
    textareaRef.current.focus();
  };

  const highlightMentions = (text) => {
    // Highlight @mentions in comment text
    const parts = text.split(/(@[a-zA-Z0-9._-]+)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('@')) {
        return (
          <span key={idx} className="text-blue-600 font-medium bg-blue-50 px-1 rounded">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(mentionSearch.toLowerCase()) ||
    user.name?.toLowerCase().includes(mentionSearch.toLowerCase()) ||
    (user.first_name && user.first_name.toLowerCase().includes(mentionSearch.toLowerCase())) ||
    (user.last_name && user.last_name.toLowerCase().includes(mentionSearch.toLowerCase()))
  ).slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading comments...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Comment Input */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={commentText}
              onChange={handleTextChange}
              placeholder="Add a comment... (use @ to mention teammates)"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              disabled={submitting}
            />
            
            {/* @Mention Dropdown - positioned below the textarea */}
            {showMentions && filteredUsers.length > 0 && (
              <div 
                className="absolute z-50 left-0 w-64 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                style={{ top: '100%', marginTop: '4px' }}
              >
                {filteredUsers.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => insertMention(user)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center gap-2 transition-colors"
                  >
                    <User className="w-4 h-4 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{user.email}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Tip: Type @ to mention a teammate
            </p>
            <button
              type="submit"
              disabled={!commentText.trim() || submitting}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Posting...' : 'Post Comment'}
            </button>
          </div>
        </form>
      </div>

      {/* Comments List */}
      {comments.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-600">No comments yet</p>
          <p className="text-xs text-gray-500 mt-1">Start the conversation</p>
        </div>
      ) : (
        <div className="space-y-3">
          {comments.map((comment) => (
            <div key={comment.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium text-blue-600">
                    {comment.user_name?.charAt(0) || 'U'}
                  </span>
                </div>

                {/* Comment Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        {comment.user_name || 'Unknown User'}
                      </span>
                      <span className="text-xs text-gray-500 ml-2">
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                      {comment.is_edited && (
                        <span className="text-xs text-gray-400 ml-2">(edited)</span>
                      )}
                    </div>
                    
                    <button
                      onClick={() => handleDelete(comment.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {highlightMentions(comment.comment_text)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OpportunityComments;
